import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useAuthService } from "./AuthContext";
import WebSocketHandler from "../Helpers/websocket/WebSocketHandler";
import {
  getQcWebsockerUrlApi,
  getWebsockerUrlApi,
} from "../API/websocket/getWebsocketurl";
import { useLocation } from "react-router";
import { useOnlineStatus } from "../Helpers/useOnlineStatus";

export const WebSocketServiceContext = createContext({
  add_observer: () => { },
  remove_observer: () => { },
  getQcWebsocketUrl: () => { },
  sendCloseCallMessage: () => { },
  sendTranscribedMessage: () => { },
  sendConferenceTranscribedMessage: () => { },
  sendConferenceLanguageUpdate: () => { },
  sendConferenceSpeakerResync: () => { },
  connectHandler: () => { },
  sendCancelSpeakMessage: () => { },
  sendRequestSpeakMessage: () => { },
  sendReorderSpeakersMessage: () => { },
  sendRaiseHandMessage: () => { },
  sendLowerHandMessage: () => { },
  sendSyncRaisedHandsMessage: () => { },
  addConnectListener: () => () => { },
  sendApproveJoinMeetingMessage: () => { },
  sendDenyJoinMeetingMessage: () => { },
  sendTranslationEnabledMessage: () => { },
  sendMoodMessage: () => { },
  sendVoiceEnabledMessage: () => { },
  updateConferenceChannelSubscription: () => { },
  clearConferenceChannelSubscription: () => { },
});

const WebSocketServiceProvider = ({ children }) => {
  const { user } = useAuthService();
  const isOnline = useOnlineStatus();
  const handler = new WebSocketHandler();
  const location = useLocation();
  const refreshSessionIntervalRef = useRef(null);
  const lastConnectedUserIdRef = useRef(null);
  const websocketDisconnectedRef = useRef(() => {});

  const add_observer = useCallback((observer) => {
    handler.addObserver(observer);
  }, [handler]);

  const remove_observer = useCallback((observer) => {
    handler.removeObserver(observer);
  }, [handler]);

  const sendCloseCallMessage = useCallback((uuid) => {
    handler.sendCloseCallMessage(uuid);
  }, [handler]);

  const sendCancelSpeakMessage = useCallback((uuid, channelName) => {
    handler.sendCancelSpeakMessage(uuid, channelName);
  }, [handler]);
  const sendRequestSpeakMessage = useCallback((uuid, payload = {}, channelName) => {
    handler.sendRequestSpeakMessage(uuid, payload, channelName);
  }, [handler]);
  const sendReorderSpeakersMessage = useCallback((uuid, userIds, channelName) => {
    handler.sendReorderSpeakersMessage(uuid, userIds, channelName);
  }, [handler]);

  const sendRaiseHandMessage = useCallback((uuid, channelName) => {
    handler.sendRaiseHandMessage(uuid, channelName);
  }, [handler]);

  const sendLowerHandMessage = useCallback((uuid, channelName) => {
    handler.sendLowerHandMessage(uuid, channelName);
  }, [handler]);

  const sendSyncRaisedHandsMessage = useCallback((uuid, channelName) => {
    handler.sendSyncRaisedHandsMessage(uuid, channelName);
  }, [handler]);

  const addConnectListener = useCallback((listener) => {
    return handler.addConnectListener(listener);
  }, [handler]);

  const sendApproveJoinMeetingMessage = useCallback((meetingId, userId) => {
    handler.sendApproveJoinMeetingMessage(meetingId, userId);
  }, [handler]);

  const sendDenyJoinMeetingMessage = useCallback((meetingId, userId) => {
    handler.sendDenyJoinMeetingMessage(meetingId, userId);
  }, [handler]);
  const sendTranslationEnabledMessage = useCallback((uuid, enabled, translationType, channelName) => {
    handler.sendTranslationEnabled(uuid, enabled, translationType, channelName);
  }, [handler]);

  const sendMoodMessage = useCallback((callUuid, mood) => {
    handler.sendMoodMessage(callUuid, mood);
  }, [handler]);

  const sendVoiceEnabledMessage = useCallback((userId, voiceEnabled) => {
    handler.sendVoiceEnabledMessage(userId, voiceEnabled);
  }, [handler]);

  const updateConferenceChannelSubscription = useCallback((callUuid, languageKey, joinMode) => {
    handler.updateConferenceChannelSubscription(callUuid, languageKey, joinMode);
  }, [handler]);

  const clearConferenceChannelSubscription = useCallback((callUuid, languageKey, joinMode) => {
    handler.clearConferenceChannelSubscription(callUuid, languageKey, joinMode);
  }, [handler]);

  const sendTranscribedMessage = useCallback((
    callUuid,
    userId,
    deviceSentDate,
    message
  ) => {
    handler.sendTranscribedMessage(callUuid, userId, deviceSentDate, message);
  }, [handler]);

  const sendConferenceTranscribedMessage = useCallback((
    callUuid,
    userId,
    deviceSentDate,
    message
  ) => {
    handler.sendConferenceTranscribedMessage(callUuid, userId, deviceSentDate, message);
  }, [handler]);

  const sendConferenceLanguageUpdate = useCallback((callUuid, languageKey) => {
    handler.sendConferenceLanguageUpdate(callUuid, languageKey);
  }, [handler]);

  const sendConferenceSpeakerResync = useCallback((callUuid, userId) => {
    handler.sendConferenceSpeakerResync(callUuid, userId);
  }, [handler]);

  const connectHandler = useCallback(() => {
    handler.disconnect();
    handler.connect(() => {
      websocketDisconnectedRef.current();
    });
  }, [handler]);

  const websocketDisconnected = () => {
    console.log(
      "websocket disconnected - handler is connected:",
      handler.isConnected()
    );
    refreshSessionUntilSuccess();
  };

  const getWebsocketUrl = useCallback(async (onSucces, errorCallback) => {
    try {
      await getWebsockerUrlApi(
        function (response) {
          onSucces(response);
        },
        function (error) {
          errorCallback(error);
        }
      );
    } catch (err) {
      errorCallback();
    }
  });

  const getQcWebsocketUrl = useCallback(
    async (conversation_id, onSucces, errorCallback) => {
      try {
        await getQcWebsockerUrlApi(
          conversation_id,
          function (response) {
            // LocalStorage.setWsLink(response.data.url);
            sessionStorage.setItem("wsLink", response.data.url);
            handler.connect(() => {
              websocketDisconnectedRef.current();
            });

            onSucces(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback();
      }
    },
    [handler]
  );

  const refreshSession = () => {
    if (user && !location.pathname.includes("quick-chat")) {
      if (handler.isConnected() && lastConnectedUserIdRef.current === user.id) {
        return;
      }
      getWebsocketUrl(
        function (response) {
          // LocalStorage.setWsLink(response.data.url);
          sessionStorage.setItem("wsLink", response.data.url);
          lastConnectedUserIdRef.current = user.id;
          handler.connect(() => {
            websocketDisconnected();
          });
        },
        function (error) {
          console.log(error, "error");
        }
      );
    } else {
      // LocalStorage.setWsLink(null);
      sessionStorage.setItem("wsLink", null);
      lastConnectedUserIdRef.current = null;
      handler.disconnect();
    }
  };

  const refreshSessionUntilSuccess = () => {
    clearInterval(refreshSessionIntervalRef.current);
    refreshSessionIntervalRef.current = setInterval(() => {
      console.log("refreshSessionUntilSuccess");
      console.log("session storage", sessionStorage.getItem("wsLink"));
      if (sessionStorage.getItem("wsLink") && handler.isConnected()) {
        clearInterval(refreshSessionIntervalRef.current);
        return;
      }

      if (user && !location.pathname.includes("quick-chat")) {
        getWebsocketUrl(
          function (response) {
            console.log("refreshSessionUntilSuccess - got response", response);
            sessionStorage.setItem("wsLink", response.data.url);
            lastConnectedUserIdRef.current = user.id;
            handler.connect(() => {
              websocketDisconnected();
            });
          },
          function (error) {
            console.log(error, "error");
            console.log("refreshSessionUntilSuccess - failed", error);
          }
        );
      } else {
        sessionStorage.setItem("wsLink", null);
        handler.disconnect();
        console.log("refreshSessionUntilSuccess - user is null");
        clearInterval(refreshSessionIntervalRef.current);
      }
    }, 5000);
  };

  useEffect(() => {
    refreshSession();
  }, [user]);

  useEffect(() => {
    if (isOnline) {
      refreshSession();
    }
  }, [isOnline]);

  useEffect(() => {
    websocketDisconnectedRef.current = websocketDisconnected;
  });

  const contextValue = useMemo(
    () => ({
      add_observer,
      remove_observer,
      getQcWebsocketUrl,
      sendCloseCallMessage,

      sendTranscribedMessage,
      sendConferenceTranscribedMessage,
      sendConferenceLanguageUpdate,
      sendConferenceSpeakerResync,
      handler,
      connectHandler,
      sendCancelSpeakMessage,
      sendRequestSpeakMessage,
      sendReorderSpeakersMessage,
      sendRaiseHandMessage,
      sendLowerHandMessage,
      sendSyncRaisedHandsMessage,
      addConnectListener,
      sendDenyJoinMeetingMessage,
      sendApproveJoinMeetingMessage,
      sendTranslationEnabledMessage,
      sendMoodMessage,
      sendVoiceEnabledMessage,
      updateConferenceChannelSubscription,
      clearConferenceChannelSubscription,
    }),
    [
      add_observer,
      remove_observer,
      getQcWebsocketUrl,
      sendCloseCallMessage,
      sendTranscribedMessage,
      sendConferenceTranscribedMessage,
      sendConferenceLanguageUpdate,
      sendConferenceSpeakerResync,
      handler,
      connectHandler,
      sendCancelSpeakMessage,
      sendRequestSpeakMessage,
      sendReorderSpeakersMessage,
      sendRaiseHandMessage,
      sendLowerHandMessage,
      sendSyncRaisedHandsMessage,
      addConnectListener,
      sendDenyJoinMeetingMessage,
      sendApproveJoinMeetingMessage,
      sendTranslationEnabledMessage,
      sendMoodMessage,
      sendVoiceEnabledMessage,
      updateConferenceChannelSubscription,
      clearConferenceChannelSubscription,
    ]
  );

  return (
    <WebSocketServiceContext.Provider value={contextValue}>
      {children}
    </WebSocketServiceContext.Provider>
  );
};
export const useWebSocketDataService = () =>
  useContext(WebSocketServiceContext);
export default WebSocketServiceProvider;
